import { createSlice } from '@reduxjs/toolkit'

function updateAuthInLocalStorage(newAuth) {
  localStorage.setItem('auth', JSON.stringify(newAuth))
}

/**
 * Picks the tenant to work in when the current selection is missing or stale.
 *
 * This used to be TenantSelector's mount effect, which was fine while the selector sat in the
 * sidebar and therefore rendered on every page. It now renders only on tenant-scoped pages, so the
 * default has to be established wherever the tenant list arrives — otherwise a user landing on (or
 * reloading) a page without the selector would have no tenant selected at all.
 */
function ensureSelectedTenant(state) {
  if (!state || !Array.isArray(state.tenants) || state.tenants.length === 0) return
  if (state.tenants.some((t) => t && t.id === state.tenant)) return
  state.tenant =
    state.tenants.find((t) => t?.status === 'active')?.id || state.tenants[0]?.id || null
}

const authSlice = createSlice({
  name: 'auth',
  initialState: null,
  reducers: {
    set(state, { payload }) {
      console.log('[authSlice.set] Setting auth state:', {
        hasPayload: !!payload,
        tenantsLength: payload?.tenants?.length,
        tenant: payload?.tenant,
      })
      // Both login and the localStorage rehydrate in App.js funnel through here, so this is the
      // one place that sees every tenant list.
      ensureSelectedTenant(payload)
      updateAuthInLocalStorage(payload)
      return payload
    },
    selectTenant(state, { payload }) {
      console.log('[authSlice.selectTenant] Selecting tenant:', payload)
      state.tenant = payload
      updateAuthInLocalStorage(state)
    },
    setTenants(state, { payload }) {
      console.log('[authSlice.setTenants] Setting tenants:', {
        payloadLength: payload?.length,
        isArray: Array.isArray(payload),
      })
      // Filter out any invalid tenants (missing required fields)
      const validTenants = Array.isArray(payload)
        ? payload.filter((t) => t && t.id && t.name && t.code)
        : []
      console.log('[authSlice.setTenants] Valid tenants after filter:', validTenants)
      state.tenants = validTenants.map(({ id, name, code, status }) => ({ id, name, code, status }))

      ensureSelectedTenant(state)
      updateAuthInLocalStorage(state)
    },
    addUpdateTenant(state, { payload }) {
      // Ensure state.tenants is an array
      const tenantsClone = Array.isArray(state.tenants) ? [...state.tenants] : []
      const foundTenant = tenantsClone.find((t) => t && t.id === payload.id)

      if (foundTenant) {
        foundTenant.name = payload.name
        if (payload.code) foundTenant.code = payload.code
        if (payload.status) foundTenant.status = payload.status
      } else {
        tenantsClone.push(payload)
      }

      state.tenants = tenantsClone
      updateAuthInLocalStorage(state)
    },
    updateCurrentTenantData(state, { payload }) {
      // Update the full tenant data if this is the currently selected tenant
      if (state.tenant === payload.id) {
        state.currentTenantData = payload
        updateAuthInLocalStorage(state)
      }
    },
    setUser(state, { payload }) {
      state.user = payload
      updateAuthInLocalStorage(state)
    },
    setUserCompany(state, { payload }) {
      const updatedCompany = {
        ...state.user.company,
        ...payload,
      }
      state.user.company = updatedCompany

      updateAuthInLocalStorage(state)
    },
    updateTokens(state, { payload }) {
      // Update tokens in state (payload should be { access: {...}, refresh: {...} })
      if (state && payload) {
        state.tokens = payload
        updateAuthInLocalStorage(state)
        console.log('[authSlice.updateTokens] Tokens updated in Redux state')
      }
    },
  },
})

export const authActions = authSlice.actions

export default authSlice
